<template>
    <oss-dialog
        v-model="dialog"
        title="Nouvel état financier"
        :minWidth="'40vw'"
        @previous="previous"
        :previous-display="!!(step && step < 4)"
        @close="reset"
    >
        <v-window v-model="step">
            <v-window-item :value="0">
                <v-row class="d-flex align-center px-8 pb-2" no-gutters>
                    <v-col class="d-flex pr-1" cols="4" />
                    <v-col class="d-flex justify-center pr-1" cols="4"> Standard </v-col>
                    <v-col class="d-flex justify-center pr-1" cols="4"> Détaillé </v-col>
                </v-row>
                <template v-if="apolloIsFinished">
                    <template v-for="exportableOA of exportableOperatingAccountJsons">
                        <v-row
                            class="px-8"
                            no-gutters
                            :key="exportableOA.id"
                            v-if="exportableOA.name"
                        >
                            <v-col class="d-flex align-center pr-1" cols="4">
                                {{ exportableOA.name }}
                            </v-col>
                            <v-col class="d-flex justify-center" cols="4">
                                <v-checkbox
                                    v-model="exportableOA.standard"
                                    class="my-0"
                                    hide-details
                                    density="compact"
                                    :data-cy="
                                        'AccountingBooklet-exportableOA.standard' + exportableOA.id
                                    "
                                />
                            </v-col>
                            <v-col class="d-flex justify-center" cols="4">
                                <v-checkbox
                                    v-model="exportableOA.detailed"
                                    class="my-0"
                                    hide-details
                                    density="compact"
                                    :data-cy="
                                        'AccountingBooklet-exportableOA.detailed' + exportableOA.id
                                    "
                                />
                            </v-col>
                        </v-row>
                    </template>
                    <template v-if="partitionTags && partitionTags.length">
                        <v-row class="py-4 d-flex justify-center" no-gutters>
                            <v-col cols="11">
                                <v-divider />
                            </v-col>
                        </v-row>
                        <v-row class="px-8" no-gutters>
                            <v-col class="d-flex align-center pr-1" cols="4">
                                Tableau analytique
                            </v-col>
                            <v-col class="d-flex justify-center" cols="4">
                                <v-checkbox
                                    v-model="operatingAccountFromPartition.standard"
                                    class="my-0"
                                    hide-details
                                />
                            </v-col>
                            <v-col class="d-flex justify-center" cols="4">
                                <v-checkbox
                                    v-model="operatingAccountFromPartition.detailed"
                                    class="my-0"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <template
                            v-if="
                                operatingAccountFromPartition.standard ||
                                operatingAccountFromPartition.detailed
                            "
                        >
                            <v-row class="pl-5 pt-4" no-gutters>
                                <v-col cols="6">
                                    <v-select-filled
                                        v-model="operatingAccountFromPartition.selectedTables"
                                        :items="tableTypes"
                                        item-value="id"
                                        label="Table"
                                        hide-details
                                        multiple
                                        class="text-caption"
                                        density="compact"
                                        :item-title="(item) => item.name"
                                    >
                                    </v-select-filled>
                                </v-col>
                            </v-row>
                            <v-row class="pl-5 pt-4" no-gutters>
                                <v-col cols="6">
                                    <v-select-filled
                                        v-model="operatingAccountFromPartition.selectedPartition"
                                        :items="partitionTags"
                                        item-value="id"
                                        label="Partition"
                                        hide-details
                                        class="text-caption"
                                        density="compact"
                                        :item-title="(item) => item.name"
                                    >
                                    </v-select-filled>
                                </v-col>
                            </v-row>
                            <v-row class="pl-5 pt-4" no-gutters>
                                <v-col cols="6">
                                    <v-select-filled
                                        v-model="operatingAccountFromPartition.selectedTags"
                                        :items="tags"
                                        item-value="id"
                                        label="Etiquette"
                                        hide-details
                                        multiple
                                        class="text-caption"
                                        density="compact"
                                        :item-title="(item) => item.name"
                                    >
                                    </v-select-filled>
                                </v-col>
                            </v-row>
                        </template>
                    </template>
                </template>
                <v-row class="d-flex align-center px-8" no-gutters v-else>
                    <v-col class="d-flex align-center justify-center">
                        <v-progress-circular
                            :size="70"
                            :width="7"
                            color="primary"
                            indeterminate
                            style="margin: 24px"
                        />
                    </v-col>
                </v-row>
            </v-window-item>
            <v-window-item :value="1">
                <v-row no-gutters class="d-flex justify-center">
                    <v-col cols="12" class="d-flex justify-center">
                        <span>Nombre de périodes</span>
                    </v-col>
                    <v-col cols="12" class="d-flex justify-center">
                        <v-radio-group
                            v-model="periodsNumber"
                            inline
                            class="d-flex justify-center"
                            color="primary"
                        >
                            <v-radio label="1" class="pr-2" :value="1" />
                            <v-radio
                                label="2"
                                class="pr-2"
                                :disabled="previousAccountingPeriodCount < 1"
                                :value="2"
                            />
                            <v-radio
                                label="3"
                                class="pr-2"
                                :disabled="previousAccountingPeriodCount < 2"
                                :value="3"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center py-2">
                    <v-col cols="12" class="d-flex justify-center pb-2">
                        <span>Choisissez la période N</span>
                    </v-col>
                    <v-col
                        :cols="accountingClosings && accountingClosings.length ? '6' : '8'"
                        class="d-flex justify-center"
                    >
                        <v-select-filled
                            v-model="accountingPeriodSelected"
                            :items="accountingPeriods"
                            :item-title="(item) => formatedAccountingPeriod(item)"
                            return-object
                            label="Exercice comptable"
                            class="pr-1"
                            density="compact"
                        />
                    </v-col>
                    <v-col
                        cols="6"
                        class="d-flex justify-center"
                        v-if="accountingClosings && accountingClosings.length"
                    >
                        <v-select-filled
                            v-model="accountingClosingSelected"
                            :items="accountingClosings"
                            :item-title="(item) => item.name"
                            return-object
                            label="Situation comptable"
                            class="pl-1"
                            density="compact"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center pb-2">
                    <v-col cols="4" class="d-flex justify-center">
                        <v-select-filled
                            v-model="startDate"
                            :items="startMonthlyDates"
                            label="Date de début"
                            class="pr-1"
                            hide-details
                            :disabled="
                                atLeastOneBalanceSheetSelected ||
                                !accountingPeriodSelected ||
                                (!!accountingClosingSelected && !!accountingClosingSelected.id)
                            "
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="4" class="d-flex justify-center">
                        <v-select-filled
                            v-model="endDate"
                            :items="endMonthlyDates"
                            label="Date de fin"
                            class="pl-1"
                            hide-details
                            :disabled="
                                !accountingPeriodSelected ||
                                (!!accountingClosingSelected && !!accountingClosingSelected.id)
                            "
                            density="compact"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center pb-2">
                    <v-col v-if="periodsNumber > 1" cols="6" class="d-flex justify-center">
                        <v-select-filled
                            :label="periodNMinus(1)"
                            disabled
                            class="pr-1"
                            density="compact"
                        />
                    </v-col>
                    <v-col v-if="periodsNumber > 1" cols="6" class="d-flex justify-center">
                        <v-select-filled
                            v-model="displayNMinusCompletePeriod"
                            :items="[
                                { title: 'Période complète', value: true },
                                { title: 'Même période que N', value: false },
                            ]"
                            class="pl-1"
                            density="compact"
                        />
                    </v-col>
                    <v-col v-if="periodsNumber > 2" cols="8" class="d-flex justify-center">
                        <v-select-filled :label="periodNMinus(2)" disabled density="compact" />
                    </v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center" v-if="outputs && outputs.length">
                    <v-col cols="8" class="d-flex justify-center pb-2">
                        <v-divider />
                    </v-col>
                    <v-col cols="12" class="d-flex justify-center pb-2">
                        <span>Intégration d'un budget</span>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-center">
                        <v-menu>
                            <template v-slot:activator="{ props: menu }">
                                <v-text-field-filled
                                    :hide-details="true"
                                    v-bind="{ ...menu }"
                                    readonly
                                    :append-inner-icon="'arrow_drop_down'"
                                    :model-value="budgets[selectedBudget].name"
                                    density="compact"
                                />
                            </template>
                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in budgets"
                                    :key="index"
                                    @click="
                                        id = item.id;
                                        selectedBudget = index;
                                    "
                                    class="oss-list-item"
                                >
                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-window-item>
            <v-window-item :value="2">
                <v-row>
                    <v-checkbox
                        v-model="exportWithProjectWatermark"
                        :ripple="false"
                        class="ml-1 mb-2"
                        :title="$t('shared.exportWithProjectWatermark')"
                        :label="$t('shared.exportWithProjectWatermark')"
                    />
                </v-row>
                <v-list subheader lines="two">
                    <v-list-item
                        :class="
                            !$vuetify.display.mobile
                                ? 'mb-2 oss-list-item'
                                : 'mb-2 px-2 oss-list-item'
                        "
                        :disabled="exportWithProjectWatermark"
                        @click="
                            exportClass = 'excel';
                            step = 3;
                        "
                    >
                        <template #prepend>
                            <v-icon color="secondary"> $fa-file-excel </v-icon>
                        </template>
                        <v-list-item-title>Export Excel</v-list-item-title>
                        <v-list-item-subtitle>Exporter au format .xlsx</v-list-item-subtitle>

                        <template v-slot:append>
                            <v-btn-next />
                        </template>
                    </v-list-item>
                    <v-list-item
                        :class="
                            !$vuetify.display.mobile
                                ? 'mb-2 oss-list-item rounded-lg'
                                : 'mb-2 px-2 oss-list-item rounded-lg'
                        "
                        @click="
                            exportClass = 'DOCX';
                            step = 3;
                        "
                        v-if="
                            !operatingAccountFromPartition.standard &&
                            !operatingAccountFromPartition.detailed
                        "
                    >
                        <template #prepend>
                            <v-icon color="secondary"> $fa-file-word </v-icon>
                        </template>
                        <v-list-item-title>Docx</v-list-item-title>
                        <v-list-item-subtitle>Exporter au format .docx</v-list-item-subtitle>

                        <template v-slot:append>
                            <v-btn-next />
                        </template>
                    </v-list-item>
                    <v-list-item
                        :class="
                            !$vuetify.display.mobile
                                ? 'mb-2 oss-list-item rounded-lg'
                                : 'mb-2 px-2 oss-list-item rounded-lg'
                        "
                        @click="
                            exportClass = 'PDF';
                            step = 3;
                        "
                        v-if="
                            !operatingAccountFromPartition.standard &&
                            !operatingAccountFromPartition.detailed
                        "
                    >
                        <template #prepend>
                            <v-icon color="secondary"> $fa-file-pdf </v-icon>
                        </template>
                        <v-list-item-title>Pdf</v-list-item-title>
                        <v-list-item-subtitle>Exporter au format .pdf</v-list-item-subtitle>

                        <template v-slot:append>
                            <v-btn-next />
                        </template>
                    </v-list-item>
                </v-list>
            </v-window-item>
            <v-window-item :value="3">
                <template v-if="exportClass !== 'excel'">
                    <v-row no-gutters>
                        <v-col cols="4" class="d-flex align-center">
                            <h3>Titre personnalisé :</h3>
                        </v-col>
                        <v-col cols="8" class="d-flex align-center">
                            <v-text-field-filled v-model="title" />
                        </v-col>
                    </v-row>
                    <v-checkbox
                        v-model="options"
                        label="Avec page de garde"
                        value="firstpage"
                        density="compact"
                        hide-details
                    />
                    <v-checkbox
                        v-model="options"
                        label="Arrondir les montants"
                        value="round"
                        density="compact"
                        hide-details
                    />
                    <v-checkbox
                        v-model="options"
                        label="Ajouter l'attestation de l'expert-comptable"
                        value="certificate"
                        density="compact"
                        hide-details
                    />
                    <v-checkbox
                        v-model="options"
                        label="Ajouter le sommaire"
                        value="summary"
                        density="compact"
                        hide-details
                        v-if="exportClass === 'DOCX'"
                    />
                    <v-checkbox
                        v-model="options"
                        label="Ajouter un résumé des infos importantes"
                        value="dashboardInfos"
                        density="compact"
                        hide-details
                    />
                </template>
                <v-row v-else-if="!onlyBalanceSheetSelected" no-gutters>
                    <v-col>
                        <v-checkbox
                            v-model="options"
                            label="Afficher les montants mensuels"
                            value="monthly"
                            density="compact"
                            hide-details
                        />
                    </v-col>
                </v-row>

                <!-- <v-list-item-title> Aucun paramétrage avec cette sélection </v-list-item-title> -->
                <v-row v-if="showTagsSelector" no-gutters>
                    <v-col>
                        <oss-tags-selector-filled
                            v-model="selectedTags"
                            :items="usedTagsFather"
                            :label="'Filtrer par étiquettes (SIG / CR / Flash)'"
                            itemTitle="text"
                        />
                    </v-col>
                </v-row>
            </v-window-item>
        </v-window>

        <template v-slot:actions>
            <v-row no-gutters>
                <v-col cols="12" class="pr-2" v-if="step !== 2">
                    <v-btn-primary
                        v-if="step < 2"
                        rounded
                        block
                        :disabled="disableNextWindowItem"
                        @click="step++"
                        data-cy="AccountingBooklet-next"
                        size="x-large"
                        >Suivant</v-btn-primary
                    >
                    <v-btn-primary
                        v-else-if="step === 3"
                        :loading="loading"
                        rounded
                        block
                        @click="createBooklet(exportClass)"
                        data-cy="AccountingBooklet-export"
                        size="x-large"
                        >Exporter</v-btn-primary
                    >
                </v-col>
                <v-col cols="12" class="pr-2" v-else> </v-col>
            </v-row>
        </template>
    </oss-dialog>
</template>

<script lang="ts">
import { gql } from "graphql-tag";
import moment from "moment-timezone";

import OPERATING_ACCOUNT_QUERY from "../../graphql/tag/OperatingAccounts.gql";
import SCENARIO_NAMES from "../../graphql/budget/BudgetScenario.gql";

import {
    getAccountingPeriodEndMonths,
    getAccountingPeriodStartMonths,
} from "../../services/accountingPeriods";

import getEnv from "../../env";
import { Apollo } from "@/decorators";
import { NotificationBase } from "../NotificationBase";
import { getDBFormattedDate } from "@/services/dateServices";
import OPERATING_ACCOUNT_FROM_PARTITION from "../../graphql/dashboardAccounting/OperatingAccountFromPartition.gql";
import { DashBoardTable } from "../../../../backend/src/common/dashboard/dashboardTable";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop, Model } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

const env = getEnv();

@Component({
    emits: ["exportStarted", "back"],
})
export class AccountingBooklet extends NotificationBase {
    @Model() value: boolean;
    @Prop({ default: null }) tabFather: any;
    @Prop({ default: null }) startDateFather: string;
    @Prop({ default: null }) endDateFather: string;
    @Prop({ default: null }) optionsFather: any;
    @Prop({ default: [] }) tagsFather: any;
    @Prop({ default: [] }) usedTagsFather: any;
    @Prop({ default: [] }) partitionTags: any;
    @Prop({ default: [] }) periods: any;
    @Prop({
        default: {
            selectedTable: null,
            selectedPartition: null,
            selectedTags: [],
        },
    })
    dataFromOAFP: {
        selectedTable: string;
        selectedPartition: string;
        selectedTags: string[];
    };

    loading = false;

    step = 0;

    // BalanceSheetOutput:any = { standard: false, detailed: false }

    operatingAccountCustom = {
        standard: false,
        detailed: false,
        operatingAccountId: "",
        partitionId: "",
        isOperatingAccountCustom: true,
    };

    apolloIsFinished = false;

    periodsNumber = 1;
    displayNMinusCompletePeriod: boolean = false;
    accountingPeriod = true;
    accountingPeriodSelected = null;
    accountingClosingSelected = null;
    selectedBudget: number = 0;

    startDate = null;
    endDate = null;

    customTitle_: string = null;

    exportClass = "";
    // exportTabs = [{operatingAccountId: "", versions: ['standard']}]

    options = [];
    themeId = "";
    // summary = false
    // dashboardInfos = false
    // withPageNumber = false

    operatingAccountFromPartition: {
        standard: boolean;
        detailed: boolean;
        selectedTables: string[];
        selectedPartition: string;
        selectedTags: string[];
        tables: any[];
        companyName: string;
        tags: {
            id: string;
            name: string;
        }[];
    } = {
        standard: false,
        detailed: false,
        selectedTables: [],
        selectedPartition: null,
        selectedTags: [],
        tables: [],
        companyName: this.companyName,
        tags: [],
    };

    selectedTags: any = [];

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
    }

    get companyId() {
        return this.$store?.state.selected_company.id;
    }

    get companyName() {
        return this.$store?.state.selected_company.name;
    }

    get accountingPeriods() {
        return this.$store?.state.accountingPeriod.accountingPeriods;
        // return this.$store?.state.accountingPeriod.accountingPeriods.toSorted((a, b) => {
        //     return a.end < b.end ? -1 : 1;
        // });
    }

    get title() {
        if (this.customTitle_ === null) {
            if (
                moment(this.startDate).locale("fr").months() === 0 &&
                moment(this.startDate).locale("fr").dates() === 1 &&
                moment(this.endDate).locale("fr").months() === 11 &&
                moment(this.endDate).locale("fr").dates() === 31
            ) {
                return (
                    "Comptes annuels au " + moment(this.endDate).locale("fr").format("DD/MM/YYYY")
                );
            }
            return (
                "Comptes annuels du " +
                moment(this.startDate).locale("fr").format("DD/MM/YYYY") +
                " au " +
                moment(this.endDate).locale("fr").format("DD/MM/YYYY")
            );
        }
        return this.customTitle_;
    }

    set title(value: string) {
        this.customTitle_ = value;
    }

    get accountingClosings() {
        // ** this.accountingClosings[0] = {id: "", name: "Sans situation"} for select
        if (this.accountingPeriodSelected) {
            const _storedAccountingClosings = this.accountingPeriodSelected.accountingClosings;
            if (_storedAccountingClosings?.length) {
                const storedAccountingClosings = [
                    {
                        id: "",
                        name: "Sans situation",
                    },
                ];
                storedAccountingClosings.push(..._storedAccountingClosings);
                return storedAccountingClosings;
            }
        }
        this.accountingClosingSelected = null;
        return [];
    }

    get disableNextWindowItem() {
        switch (this.step) {
            case 0: {
                if (this.operatingAccountCustom.detailed || this.operatingAccountCustom.standard) {
                    return false;
                }
                if (
                    this.operatingAccountFromPartition.standard ||
                    this.operatingAccountFromPartition.detailed
                ) {
                    return false;
                }
                if (!this.outputs.length) {
                    return true;
                }
                break;
            }
            case 1: {
                if (this.operatingAccountCustom.detailed || this.operatingAccountCustom.standard) {
                    if (!this.operatingAccountCustom.partitionId) {
                        return true;
                    }
                }
                if (!this.accountingPeriod && (!this.startDate || !this.endDate)) {
                    return true;
                }
                if (this.periodNMinusError === true) {
                    return true;
                }
                break;
            }
        }
        return false;
    }

    get disableCertificateOption() {
        // no longer used
        if (
            this.outputs.find((e) => e.type === "BALANCE_SHEET") &&
            this.outputs.find((e) => e.type === "INCOME_STATEMENT")
        ) {
            return false;
        }
        return true;
    }

    get startMonthlyDates() {
        return getAccountingPeriodStartMonths(this.accountingPeriodSelected);
    }

    get endMonthlyDates() {
        return getAccountingPeriodEndMonths(this.accountingPeriodSelected);
    }

    get disableDownload() {
        return false;
    }

    get onlyBalanceSheetSelected() {
        if (this.outputs.some((el) => el.type !== "BALANCE_SHEET")) {
            return false;
        }
        return true;
    }

    get atLeastOneBalanceSheetSelected() {
        if (this.outputs.some((el) => el.type === "BALANCE_SHEET")) {
            return true;
        }
        return false;
    }

    @Watch("accountingPeriodSelected")
    adaptStartAndEndDate() {
        if (
            moment(this.startDate) < moment(this.accountingPeriodSelected.start) ||
            moment(this.startDate) > moment(this.accountingPeriodSelected.end)
        ) {
            this.startDate = moment(this.accountingPeriodSelected.start).format("YYYY-MM-DD");
        }
        if (
            moment(this.endDate) < moment(this.accountingPeriodSelected.start) ||
            moment(this.endDate) > moment(this.accountingPeriodSelected.end)
        ) {
            this.endDate = moment(this.accountingPeriodSelected.end).format("YYYY-MM-DD");
        }
    }

    @Watch("accountingClosingSelected")
    adaptStartAndEndDateForClosing() {
        if (this.accountingClosingSelected?.id) {
            this.startDate = moment(this.accountingPeriodSelected.start).format("YYYY-MM-DD");
            this.endDate = moment(this.accountingClosingSelected.endDate).format("YYYY-MM-DD");
        }
    }

    get periodNMinusError() {
        const index = this.accountingPeriods.findIndex(
            (e) => e.id === this.accountingPeriodSelected.id,
        );
        if (this.onlyBalanceSheetSelected) {
            if (this.periodsNumber > 2) {
                return true;
            }
        }
        if (this.periodsNumber > index + 1) {
            return true;
        } else {
            return false;
        }
    }

    exportWithProjectWatermark = false;

    beforeMount() {
        if (this.accountingPeriods?.length) {
            if (this.$store.state.accountingPeriod.selectedAccountingPeriod) {
                if (
                    this.accountingPeriods.find(
                        (ap) =>
                            ap.id ===
                            this.$store.state.accountingPeriod.selectedAccountingPeriod.id,
                    )
                ) {
                    this.accountingPeriodSelected =
                        this.$store.state.accountingPeriod.selectedAccountingPeriod;
                }
            }
            if (!this.accountingPeriodSelected) {
                this.accountingPeriodSelected =
                    this.accountingPeriods[this.accountingPeriods.length - 1];
            }
            if (this.accountingClosings) {
                // ** this.accountingClosings[0] = {id: "", name: "Sans situation"} for select
                this.accountingClosingSelected = this.$store.state.accountingPeriod
                    .selectedAccountingClosing?.id
                    ? this.$store.state.accountingPeriod.selectedAccountingClosing
                    : this.accountingClosings[0];
            }
        }

        //// tabFather is disabled at the moment because of reactivity issues, unchecking tab father box is not taken into account
        ////
        // if (this.tabFather?.isOperatingAccountCustom) {
        //     this.operatingAccountCustom.standard = true
        // }
        ////

        this.startDate =
            this.startDateFather ||
            moment(this.accountingPeriodSelected.start).format("YYYY-MM-DD");
        this.endDate =
            this.endDateFather || moment(this.accountingPeriodSelected.end).format("YYYY-MM-DD");
        this.selectedTags = this.tagsFather;
        if (this.optionsFather) {
            if (this.optionsFather.displayNMinusOne) {
                const accountingPeriodIndex = this.accountingPeriods.findIndex(
                    (ap) => ap.id === this.accountingPeriodSelected.id,
                );
                if (accountingPeriodIndex > 0) {
                    this.periodsNumber = 2;
                } else {
                    this.periodsNumber = 1;
                }
                if (this.optionsFather.displayNMinusCompletePeriod) {
                    this.displayNMinusCompletePeriod =
                        this.optionsFather.displayNMinusCompletePeriod;
                }
            }
            if (this.optionsFather.displayMonthly) {
                this.options.push("monthly");
            }
            if (this.optionsFather.budgetFather) {
                this.selectedBudget = this.optionsFather.budgetFather;
            }
        }

        if (this.dataFromOAFP.selectedTable) {
            this.operatingAccountFromPartition.selectedTables.push(this.dataFromOAFP.selectedTable);
        } else if (this.tableTypes?.length > 0) {
            this.operatingAccountFromPartition.selectedTables.push(this.tableTypes[0].id);
        }
        if (this.dataFromOAFP.selectedPartition) {
            this.operatingAccountFromPartition.selectedPartition =
                this.dataFromOAFP.selectedPartition;
        } else if (this.partitionTags?.length > 0) {
            this.operatingAccountFromPartition.selectedPartition = this.partitionTags[0].id;
        }
        if (this.dataFromOAFP.selectedTags) {
            this.operatingAccountFromPartition.selectedTags = this.dataFromOAFP.selectedTags;
        }
    }

    periodNMinus(number) {
        const index = this.accountingPeriods.findIndex(
            (ap) => ap.id === this.accountingPeriodSelected.id,
        );

        if (index - number >= 0) {
            return this.formatedAccountingPeriod(this.adaptDateToTheYear(index, number));
        }
        return "Il n'y a pas de période comptable correspondant à cette année";
    }

    formatedAccountingPeriod(item) {
        return (
            moment(item.start).format("DD/MM/YYYY") + " - " + moment(item.end).format("DD/MM/YYYY")
        );
    }

    getPeriods() {
        let result = [];
        if (this.accountingPeriod && this.accountingPeriodSelected) {
            const index = this.accountingPeriods.findIndex(
                (ap) => ap.id === this.accountingPeriodSelected.id,
            );
            for (let i = 0; i < this.periodsNumber; i++) {
                result.push(this.adaptDateToTheYear(index, i));
            }
        } else {
            result = [
                {
                    start: this.startDate,
                    end: this.endDate,
                },
            ];
        }
        if (this.selectedBudget) {
            if (this.budgets[this.selectedBudget].id) {
                const index = this.accountingPeriods.findIndex(
                    (ap) => ap.id === this.accountingPeriodSelected.id,
                );
                const firstPeriodStart = this.accountingPeriods[index]?.start;
                const firstPeriodEnd = this.accountingPeriods[index]?.end;
                const start = moment(firstPeriodStart).format();
                const end = moment(firstPeriodEnd).format();
                result.push({
                    start,
                    end,
                    type: "budget",
                    budgetId: this.budgets[this.selectedBudget].id,
                });
            } else if (this.budgets[this.selectedBudget].type === "freeBudget") {
                result.push({
                    // start,
                    // end,
                    type: "freeBudget",
                    // budgetId: this.budgets[this.selectedBudget].id,
                });
            }
        }
        return result;
    }

    // @Apollo({
    //     query() {
    //         return PARTITION_TAG_QUERY;
    //     },
    //     variables() {
    //         return {
    //             companyId: this.companyId
    //         };
    //     },
    //     debounce: 500,
    //     skip() {
    //         return !this.companyId
    //     },
    //     specificResult({ data, loading, networkStatus }: any) {
    //         if (!loading && data) {
    //             this.partitionTags = data.partitionTags
    //             if (this.partitionTags?.length) {
    //                 this.operatingAccountCustom.partitionId = this.partitionTags[0].id
    //             } else {
    //                 this.operatingAccountCustom.partitionId = ""
    //             }
    //         } else {
    //             this.partitionTags = []
    //             this.operatingAccountCustom.partitionId = ""
    //         }
    //     }
    // })
    // partitionTags = [];

    @Apollo({
        query() {
            return OPERATING_ACCOUNT_QUERY;
        },
        variables() {
            return {
                companyId: this.companyId,
                use: "OPERATING_ACCOUNT",
            };
        },
        debounce: 500,
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data && data.scopingOperatingAccount) {
                this.operatingAccountJsons = data.scopingOperatingAccount.data || [];

                //// tabFather is disabled at the moment because of reactivity issues, unchecking tab father box is not taken into account
                ////
                // if (this.tabFather?.isOperatingAccountCustom) {
                //     this.operatingAccountCustom.standard = true
                // }
                // if(this.tabFather?.type && this.tabFather.type !== "BALANCE_SHEET") {
                //     const alreadySelected = this.exportableOperatingAccountJsons.find(e => e.type === this.tabFather.type)
                //     alreadySelected.standard = true
                // }
                ////

                // if(this.exportTabs.length && this.exportTabs[0].operatingAccountId && this.operatingAccountJsons.length) {
                //     this.exportTabs[0].operatingAccountId = this.operatingAccountJsons[0]
                // }
            } else {
                this.operatingAccountJsons = [];
            }
            this.apolloIsFinished = true;
        },
    })
    operatingAccountJsons = [];

    get exportableOperatingAccountJsons() {
        let result = [];
        const types = ["BALANCE_SHEET", "INCOME_STATEMENT", "SIG", "FLASH_SITUATION"];
        for (const type of types) {
            const firstOAofThisType = this.operatingAccountJsons.find((e) => e.type === type);
            if (firstOAofThisType) {
                result.push(firstOAofThisType);
            }
        }
        result = result.concat(
            this.operatingAccountJsons.filter((e) => e.type === "MISCELLANEOUS"),
        );
        return result;
    }

    // @Watch('BalanceSheetOutput', { deep: true })
    // @Watch('exportableOperatingAccountJsons', { deep: true })
    // SetOutputs() {
    get outputs() {
        const result = [];
        for (const Oa of this.exportableOperatingAccountJsons) {
            if (Oa.standard || Oa.detailed) {
                const element = {
                    id: Oa.id,
                    name: Oa.name,
                    standard: Oa.standard,
                    detailed: Oa.detailed,
                    type: Oa.type,
                };
                result.push(element);
            }
        }
        return result;
    }

    getTabs() {
        const result = [...this.outputs];
        if (this.operatingAccountCustom.detailed || this.operatingAccountCustom.standard) {
            result.push(this.operatingAccountCustom);
        }

        return result;
    }

    @Apollo({
        query: SCENARIO_NAMES,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loading = loading;
            if (!loading) {
                if (data?.budgets?.length) {
                    const budgets = JSON.parse(JSON.stringify(data.budgets));
                    budgets.unshift({
                        id: "",
                        name: "Budget libre",
                        type: "freeBudget",
                        date: moment().format("YYYY-MM-DD"),
                    });
                    budgets.unshift({
                        id: "",
                        name: "Sans budget",
                        type: "",
                        date: moment().format("YYYY-MM-DD"),
                    });
                    this.budgets = budgets;
                } else {
                    this.budgets = [];
                    this.budgets.unshift({
                        id: "",
                        name: "Budget libre",
                        type: "freeBudget",
                        date: moment().format("YYYY-MM-DD"),
                    });
                    this.budgets.unshift({
                        id: "",
                        name: "Sans budget",
                        type: "",
                        date: moment().format("YYYY-MM-DD"),
                    });
                }
            }
        },
    })
    budgets: Array<any> = [];

    async createBooklet(exportClass) {
        this.loading = true;
        const periods = this.getPeriods();
        for (const period of periods) {
            period.start = getDBFormattedDate(period.start); // demander si c'est bon
            period.end = getDBFormattedDate(period.end);
        }
        if (
            this.operatingAccountFromPartition.standard ||
            this.operatingAccountFromPartition.detailed
        ) {
            const startPaths = this.operatingAccountFromPartition.selectedTags.length
                ? this.tags
                      .filter((tag) =>
                          this.operatingAccountFromPartition.selectedTags.includes(tag.id),
                      )
                      .map((tag) => tag.name)
                : "";
            this.operatingAccountFromPartition.tags = this.tags;
            for (const operatingAccountId of this.operatingAccountFromPartition.selectedTables) {
                // eslint-disable-next-line
                const result: any = await this.$apollo.query({
                    query: OPERATING_ACCOUNT_FROM_PARTITION,
                    variables: {
                        companyId: this.companyId,
                        operatingAccountId: operatingAccountId,
                        periods: this.periods,
                        partitionId: this.operatingAccountFromPartition.selectedPartition,
                        startPaths: startPaths,
                    },
                });
                if (!result || !result.data) {
                    debugger;
                }
                const output = JSON.parse(
                    JSON.stringify(result.data["operatingAccountFromPartition"].output),
                );
                this.setColumnsToDisplay(output, output.tables[0], output.periods);
                const tables = output.tables;
                const resultPeriods = output.periods;
                this.computeTableHeaders(tables, resultPeriods);
                const dashboardTable = new DashBoardTable(resultPeriods, tables);
                this.updateAmounts(dashboardTable);
                this.operatingAccountFromPartition.tables.push(tables[0]);
            }
        }
        if (this.exportWithProjectWatermark) {
            const watermark = this.$t("shared.draft").toUpperCase();
            this.options.push(`{"watermark": "${watermark}"}`);
            // we need to use JSON.stringify because this.options is a string[] in backend
        }
        const result: any = await this.$apollo.mutate({
            mutation: gql`
                mutation exportBooklet(
                    $companyId: ID!
                    $periods: Json!
                    $accountingPeriodId: ID
                    $accountingClosingId: ID
                    $tabs: [Json!]
                    $docType: String!
                    $options: [String!]
                    $customTitle: String
                    $themeId: ID
                    $tags: [Json]
                    $operatingAccountFromPartition: Json!
                ) {
                    exportBooklet(
                        data: {
                            companyId: $companyId
                            periods: $periods
                            accountingPeriodId: $accountingPeriodId
                            accountingClosingId: $accountingClosingId
                            tabs: $tabs
                            docType: $docType
                            options: $options
                            customTitle: $customTitle
                            themeId: $themeId
                            tags: $tags
                            operatingAccountFromPartition: $operatingAccountFromPartition
                        }
                    ) {
                        output
                    }
                }
            `,
            variables: {
                companyId: this.companyId,
                periods: periods,
                accountingPeriodId: this.accountingPeriodSelected?.id,
                accountingClosingId: periods[0]?.accountingClosingId
                    ? periods[0]?.accountingClosingId
                    : undefined,
                tabs: this.getTabs(),
                docType: exportClass,
                options: this.options,
                customTitle: this.customTitle_ || this.title,
                themeId: this.themeId,
                tags: this.selectedTags,
                operatingAccountFromPartition: this.operatingAccountFromPartition,
            },
        });
        if (result && !result?.data) {
            debugger;
        }
        this.loading = false;
        this.dialog = false;
        this.$emit("exportStarted");
    }

    adaptDateToTheYear(indexOfSelectedPeriod, yearsToSubstract) {
        let accountingClosingId = undefined;
        if (yearsToSubstract === 0 && this.accountingClosingSelected?.id) {
            accountingClosingId = this.accountingClosingSelected.id;
        }
        const index = indexOfSelectedPeriod - yearsToSubstract;
        const accountingPeriod = this.accountingPeriods?.[index];
        const completePeriod =
            this.displayNMinusCompletePeriod && yearsToSubstract !== 0 ? true : false;
        // const sameMonthsAsN = this.optionsFather.displayNMinusSamePeriodAsN
        return {
            accountingPeriodId: accountingPeriod?.id || undefined,
            accountingClosingId: accountingClosingId,
            start:
                this.startDate && !completePeriod
                    ? moment(this.startDate).subtract(yearsToSubstract, "year").format()
                    : accountingPeriod.start,
            end:
                this.endDate && !completePeriod
                    ? moment(this.endDate).subtract(yearsToSubstract, "year").format()
                    : accountingPeriod.end,
        };
    }

    // removeCondition(index) {
    //     this.exportTabs.splice(index, 1)
    // }

    // addCondition() {
    //     this.exportTabs.push({ operatingAccountId: "", versions: [] })
    // }

    // directDownload() {
    //     window.open(`${env.VUE_APP_BACKEND}/accountingItemExport/download/${this.accountingItemExport.id}`, '_blank')
    // }

    previous() {
        if (this.step !== 0) {
            this.step--;
        } else {
            this.$emit("back");
        }
    }

    reset() {
        this.step = 0;
        this.loading = false;

        // this.outputs = []

        this.periodsNumber = 1;
        this.accountingPeriod = true;
        this.accountingPeriodSelected = null;

        this.startDate = null;
        this.endDate = null;

        this.exportClass = null;
        this.options = [];
    }

    get previousAccountingPeriodCount() {
        if (this.accountingPeriodSelected) {
            const index = this.accountingPeriods.findIndex(
                (ap) => ap.id === this.accountingPeriodSelected.id,
            );
            if (index === -1) {
                return 0;
            }
            return index;
        }
        return 0;
    }

    //
    //
    //
    get tableTypes() {
        const tableTypes = [
            {
                id: "",
                name: "Comptes comptables",
            },
        ];
        if (this.OA_FlashSituation) {
            tableTypes.push(this.OA_FlashSituation);
        }
        if (this.OA_SIG) {
            tableTypes.push(this.OA_SIG);
        }
        if (this.OA_IncomeStatement) {
            tableTypes.push(this.OA_IncomeStatement);
        }
        return tableTypes;
    }

    get tags() {
        return (
            this.partitionTags?.find(
                (pt) => pt.id === this.operatingAccountFromPartition?.selectedPartition,
            ).tags || null
        );
    }

    get OA_SIG() {
        if (this.operatingAccountJsons) {
            const result = this.operatingAccountJsons.filter((e) => e.type === "SIG");
            if (result && result.length) {
                return result[0];
            }
        }
        return null;
    }

    get OA_IncomeStatement() {
        if (this.operatingAccountJsons) {
            const result = this.operatingAccountJsons.filter((e) => e.type === "INCOME_STATEMENT");
            if (result && result.length) {
                return result[0];
            }
        }
        return null;
    }

    get OA_FlashSituation() {
        if (this.operatingAccountJsons) {
            const result = this.operatingAccountJsons.filter((e) => e.type === "FLASH_SITUATION");
            if (result && result.length) {
                return result[0];
            }
        }
        return null;
    }

    get showTagsSelector() {
        if (this.exportClass !== "excel") {
            return false;
        }
        if (this.$vuetify.display.mobile) {
            return false;
        }
        if (
            this.exportableOperatingAccountJsons?.find(
                (operation) =>
                    ["INCOME_STATEMENT", "SIG", "FLASH_SITUATION"].includes(operation.type) &&
                    operation.standard,
            )
        ) {
            return true;
        }
        return false;
    }

    setColumnsToDisplay(output, table, periods) {
        const columnsToDisplay = [];
        for (const pi in periods) {
            columnsToDisplay.push([]);
            for (const ci in table.columns) {
                if (
                    output.tables.length === 1 ||
                    pi === "0" ||
                    table.columns[ci].key === "netAmounts"
                ) {
                    columnsToDisplay[pi].push(table.columns[ci]);
                }
            }
        }
        table.columnsToDisplay = columnsToDisplay;
    }

    computeTableHeaders(tables, resultPeriods) {
        for (const table of tables) {
            const firstHeaders: any = [{ text: "", value: "", align: "left", width: "40%" }];
            const headers: any = [
                { text: "Postes", value: "name", align: "left", sortable: false, width: "40%" },
            ];
            let pi = 0;
            for (const period of resultPeriods) {
                let columnCount = 0;
                for (const col of table.columns) {
                    if (table.name !== "Actif" || pi === 0 || col.key === "netAmounts") {
                        // pour ne pas afficher brut et net des années précédentes dans le Bilan
                        let text = "";
                        if (period.name) {
                            text = period.name;
                        }
                        if (col.name) {
                            if (text) text += " ";
                            text += col.name;
                        }
                        headers.push({
                            text,
                            value: "",
                            align: "right",
                            sortable: false,
                        });
                        columnCount++;
                    }
                }

                if (table.relativePercentage) {
                    headers.push({
                        text: "%",
                        value: "",
                        align: "right",
                        sortable: false,
                    });
                    columnCount++;
                }
                firstHeaders.push({
                    text: this.periodHeader(period),
                    value: "",
                    align: "center",
                    sortable: false,
                    colspan: columnCount,
                });
                pi++;
            }
            if (table.comparison) {
                if (table.comparison.difference) {
                    headers.push({
                        text: "Différence",
                        value: "difference",
                        align: "right",
                        sortable: false,
                    });
                    firstHeaders.push({ text: "", value: "", align: "left" });
                }
                if (table.comparison.percentage) {
                    headers.push({
                        text: "%",
                        value: "percentage",
                        align: "right",
                        sortable: false,
                    });
                    firstHeaders.push({ text: "", value: "", align: "left" });
                }
            }
            if (table.extraColumns) {
                for (const column of table.extraColumns) {
                    headers.push({
                        text: column.displayName,
                        value: "",
                        align: "right",
                        sortable: false,
                    });
                }
                firstHeaders.push({ text: "", value: "", align: "right" });
            }
            table.headers = [];
            table.headers.push(firstHeaders);
            table.headers.push(headers);
        }
    }

    periodHeader(period) {
        if (period.start && period.end) {
            return moment(period.start)
                .utc(false)
                .format("MM[/]YYYY")
                .concat(" - ", moment(period.end).utc(false).format("MM[/]YYYY"));
        } else {
            debugger; // git-ignore
        }
        return "XXX";
    }

    updateAmounts(dashboardTable) {
        if (dashboardTable) {
            dashboardTable.build();
        }
    }
}

export default toNative(AccountingBooklet);
</script>
